import { z } from 'zod';

// --- Input schemas ---

export const CONSULTANT_DASHBOARD_VIEW = {
  Target: 'target',
  Team: 'team',
} as const;

export const consultantDashboardViewSchema = z.enum([
  CONSULTANT_DASHBOARD_VIEW.Target,
  CONSULTANT_DASHBOARD_VIEW.Team,
]);
export type ConsultantDashboardView = z.infer<
  typeof consultantDashboardViewSchema
>;

const quarterInputBase = {
  view: consultantDashboardViewSchema.default(CONSULTANT_DASHBOARD_VIEW.Target),
  year: z.number().int().optional(),
  quarter: z.number().int().min(1).max(4).optional(),
  consultantId: z.uuid().optional(),
  // When set (with view='team'), KPI/pipeline/cancellation/rank/leaderboard
  // scope to this team's members instead of org-wide. Authorized server-side:
  // consultants may only pass a team they belong to.
  teamId: z.uuid().optional(),
  // XT-427: moodboard country selector. When set, the Rank card's company row
  // scopes to this Country group's sales consultants. Admin-only server-side.
  countryId: z.uuid().optional(),
};

export const consultantKpisInput = z.object(quarterInputBase);
export type ConsultantKpisInput = z.infer<typeof consultantKpisInput>;

export const revenueCrossCheckInput = z.object({
  ...quarterInputBase,
  currency: z.string().optional(),
});
export type RevenueCrossCheckInput = z.infer<typeof revenueCrossCheckInput>;

export const rankCrossCheckInput = z.object({
  ...quarterInputBase,
});
export type RankCrossCheckInput = z.infer<typeof rankCrossCheckInput>;

export const conversionRateCrossCheckInput = z.object({
  ...quarterInputBase,
});
export type ConversionRateCrossCheckInput = z.infer<
  typeof conversionRateCrossCheckInput
>;

export const leaderboardCrossCheckInput = z.object({
  ...quarterInputBase,
  currency: z.string().optional(),
  metric: z.enum(['totalSales', 'topDeal']).default('totalSales'),
});
export type LeaderboardCrossCheckInput = z.infer<
  typeof leaderboardCrossCheckInput
>;

export const PIPELINE_BUCKET_RANGES = {
  draft: { min: 1, max: 1 },
  proposal: { min: 2, max: 6 },
  sold: { min: 10, max: 19 },
  cancelled: { min: 20, max: 20 },
} as const;

export const consultantPipelineInput = z.object(quarterInputBase);
export type ConsultantPipelineInput = z.infer<typeof consultantPipelineInput>;

export const consultantLeaderboardInput = z.object({
  metric: z
    .enum(['totalSales', 'topDeal', 'conversionRate'])
    .default('totalSales'),
  year: z.number().int().optional(),
  quarter: z.number().int().min(1).max(4).optional(),
  limit: z.number().int().min(1).max(20).default(5),
  consultantId: z.uuid().optional(),
  teamId: z.uuid().optional(),
});
export type ConsultantLeaderboardInput = z.infer<
  typeof consultantLeaderboardInput
>;

export const consultantCancellationsInput = z.object(quarterInputBase);
export type ConsultantCancellationsInput = z.infer<
  typeof consultantCancellationsInput
>;

export const consultantQuarterClimbInput = z.object(quarterInputBase);
export type ConsultantQuarterClimbInput = z.infer<
  typeof consultantQuarterClimbInput
>;

// --- Response schemas ---

export const kpiValueSchema = z.object({
  value: z.number(),
  change: z.number(),
  previousValue: z.number(),
  sixMonthCr: z.number().optional(),
  pipelineTotal: z.number().optional(),
  soldThisQuarter: z.number().optional(),
});
export type KpiValue = z.infer<typeof kpiValueSchema>;

export const currencyValueSchema = z.object({
  currency: z.string(),
  value: z.number(),
  change: z.number(),
  previousValue: z.number(),
  // Calendar-window sold sums for the Revenue card (XT-Lisbon): this week =
  // Monday→now, this month = 1st→now, sold-family (10 ≤ stage < 20), EUR. Only
  // populated on the revenue element; undefined elsewhere. `thisMonthCount` is
  // the number of sold itineraries this month, so the FE can derive the
  // average ticket (thisMonth ÷ thisMonthCount) — XT-Lisbon round 2.
  thisWeek: z.number().optional(),
  thisMonth: z.number().optional(),
  thisMonthCount: z.number().optional(),
  goal: z.number().optional(),
  proRataPrevious: z.number().optional(),
  elapsedPct: z.number().optional(),
  daysElapsed: z.number().optional(),
  daysTotal: z.number().optional(),
  snapshotDate: z.string().nullable().optional(),
  snapshotSum: z.number().optional(),
  snapshotStages: z
    .array(
      z.object({
        salesStage: z.number(),
        amount: z.number(),
        // Null when the count is unavailable/corrupt (XT-217) → debug shows "—".
        count: z.number().nullable(),
      })
    )
    .optional(),
});
export type CurrencyValue = z.infer<typeof currencyValueSchema>;

export const teamEntrySchema = z.object({
  accountId: z.string(),
  fullName: z.string(),
  value: z.number(),
});
export type TeamEntry = z.infer<typeof teamEntrySchema>;

export const teamBreakdownSchema = z.object({
  revenue: z.array(teamEntrySchema),
  conversionRate: z.array(teamEntrySchema),
  pipelineValue: z.array(teamEntrySchema),
});
export type TeamBreakdown = z.infer<typeof teamBreakdownSchema>;

// One rolling-window cell for the Leads received/closed card: a count and its
// EUR value over the last `windowDays` days (30 / 60 / 90). XT-Lisbon round 2.
export const leadFlowWindowSchema = z.object({
  windowDays: z.number(),
  count: z.number(),
  value: z.number(),
});
export type LeadFlowWindow = z.infer<typeof leadFlowWindowSchema>;

export const consultantKpisResponseSchema = z.object({
  revenue: z.array(currencyValueSchema),
  conversionRate: kpiValueSchema,
  pipelineValue: z.array(currencyValueSchema),
  rank: z.object({
    value: z.number(),
    total: z.number(),
    percentile: z.string(),
    change: z.number(),
    distanceToLeader: z.number(),
    leaderValue: z.number(),
    countryTitle: z.string().nullable().optional(),
    noPeerGroup: z.boolean().optional(),
  }),
  // Rank within the consultant's sales team, alongside the country `rank`
  // (XT-Lisbon). `noTeam` when the consultant belongs to no sales team; the
  // FE degrades that row independently of the country row's `noPeerGroup`.
  teamRank: z
    .object({
      value: z.number(),
      total: z.number(),
      change: z.number(),
      teamTitle: z.string().nullable(),
      noTeam: z.boolean(),
    })
    .optional(),
  // Rank across every sales-team consultant in the org (XT-Lisbon round 2),
  // shown as "Rank in company" beside the team rank. Always resolvable, unlike
  // the country `rank` which degrades when no country group is configured.
  // XT-427: when the moodboard country selector is set, this row is scoped to
  // that country's sales consultants and `scopeTitle` carries the country title
  // for the row label (null → org-wide "Company").
  companyRank: z
    .object({
      value: z.number(),
      total: z.number(),
      change: z.number(),
      scopeTitle: z.string().nullable().optional(),
    })
    .optional(),
  // "Leads received / closed" card (XT-Lisbon round 2): itineraries assigned to
  // the resolved scope that were RECEIVED (created) / CLOSED (sold) within
  // rolling 30/60/90-day windows, each as a count and an EUR value.
  leadFlow: z
    .object({
      received: z.array(leadFlowWindowSchema),
      closed: z.array(leadFlowWindowSchema),
    })
    .optional(),
  quarterElapsedPct: z.number(),
  teamBreakdown: teamBreakdownSchema.nullable(),
});
export type ConsultantKpisResponse = z.infer<
  typeof consultantKpisResponseSchema
>;

export const pipelineStageSchema = z.object({
  name: z.string(),
  value: z.number(),
  dealCount: z.number(),
  change: z.number().nullable(),
  statusIds: z.array(z.string()),
  // Org-specific status labels (live from itinerary_status) whose sales_stage
  // falls in this bucket's range — surfaced in the debug info bubble (XT-114).
  statusLabels: z.array(z.string()),
  // Age split by itinerary creation date (XT-Lisbon): discrete bands
  // 0–3m / 3–6m / 6–9m / 9m+ from now. `value` sums to the stage `value` and
  // `count` to `dealCount`. Empty array when age data is unavailable.
  ageBuckets: z.array(
    z.object({
      label: z.string(),
      count: z.number(),
      value: z.number(),
    })
  ),
});
export type PipelineStage = z.infer<typeof pipelineStageSchema>;

export const consultantPipelineResponseSchema = z.object({
  stages: z.array(pipelineStageSchema),
  currency: z.string(),
});
export type ConsultantPipelineResponse = z.infer<
  typeof consultantPipelineResponseSchema
>;

export const leaderboardEntrySchema = z.object({
  accountId: z.string(),
  fullName: z.string(),
  value: z.number(),
});
export type LeaderboardEntry = z.infer<typeof leaderboardEntrySchema>;

export const consultantLeaderboardResponseSchema = z.object({
  entries: z.array(leaderboardEntrySchema),
  currentUserRank: z.number(),
  distanceToTop3: z.number(),
  currency: z.string(),
  noPeerGroup: z.boolean().optional(),
});
export type ConsultantLeaderboardResponse = z.infer<
  typeof consultantLeaderboardResponseSchema
>;

export const leaderboardCrossCheckEntrySchema = z.object({
  accountId: z.string(),
  firstName: z.string().nullable(),
  lastName: z.string().nullable(),
  totalSales: z.number(),
});
export type LeaderboardCrossCheckEntry = z.infer<
  typeof leaderboardCrossCheckEntrySchema
>;

export const leaderboardCrossCheckResponseSchema = z.object({
  entries: z.array(leaderboardCrossCheckEntrySchema),
  currency: z.string(),
});
export type LeaderboardCrossCheckResponse = z.infer<
  typeof leaderboardCrossCheckResponseSchema
>;

export const rankCrossCheckEntrySchema = z.object({
  accountId: z.string(),
  firstName: z.string().nullable(),
  lastName: z.string().nullable(),
  totalSales: z.number(),
  rank: z.number(),
});
export type RankCrossCheckEntry = z.infer<typeof rankCrossCheckEntrySchema>;

export const rankCrossCheckResponseSchema = z.object({
  entries: z.array(rankCrossCheckEntrySchema),
  currentUserRank: z.number(),
  currentUserTotal: z.number(),
  prevUserRank: z.number(),
  rankChange: z.number(),
  total: z.number(),
  leaderValue: z.number(),
  currency: z.string(),
});
export type RankCrossCheckResponse = z.infer<
  typeof rankCrossCheckResponseSchema
>;

export const conversionRateCrossCheckStageSchema = z.object({
  salesStage: z.number(),
  count: z.number(),
  amount: z.number(),
});
export type ConversionRateCrossCheckStage = z.infer<
  typeof conversionRateCrossCheckStageSchema
>;

export const conversionRateCrossCheckResponseSchema = z.object({
  stages: z.array(conversionRateCrossCheckStageSchema),
  totalCreatedCount: z.number(),
  totalCreatedAmount: z.number(),
  soldCount: z.number(),
  soldAmount: z.number(),
  conversionRate: z.number(),
  currency: z.string(),
});
export type ConversionRateCrossCheckResponse = z.infer<
  typeof conversionRateCrossCheckResponseSchema
>;

export const consultantCancellationsResponseSchema = z.object({
  count: z.number(),
  totalValue: z.number(),
  change: z.number(),
  currency: z.string(),
});
export type ConsultantCancellationsResponse = z.infer<
  typeof consultantCancellationsResponseSchema
>;

// "Your climb this quarter" (XT-318): cumulative sold ("booked") per week for
// the selected quarter. One point per week (empty weeks filled with 0) so the
// line is continuous; `cumulative` is monotonic non-decreasing.
export const quarterClimbPointSchema = z.object({
  weekIndex: z.number(),
  weeklySold: z.number(),
  cumulative: z.number(),
});
export type QuarterClimbPoint = z.infer<typeof quarterClimbPointSchema>;

export const consultantQuarterClimbResponseSchema = z.object({
  currency: z.string(),
  // Sum of the quarterly sales goal across the resolved scope (0 when none).
  target: z.number(),
  // Number of week buckets in the quarter (~13-14).
  totalWeeks: z.number(),
  // Current week index within the quarter, or -1 when today is outside it.
  todayWeekIndex: z.number(),
  points: z.array(quarterClimbPointSchema),
});
export type ConsultantQuarterClimbResponse = z.infer<
  typeof consultantQuarterClimbResponseSchema
>;

// AI "coach style" briefing (XT-Lisbon round 2): a short motivational summary
// of the moodboard numbers, generated server-side via the shared AI client.
export const consultantBriefingInput = z.object(quarterInputBase);
export type ConsultantBriefingInput = z.infer<typeof consultantBriefingInput>;

export const consultantBriefingResponseSchema = z.object({
  // Empty string when AI is unconfigured/unavailable — the FE hides the block.
  briefing: z.string(),
});
export type ConsultantBriefingResponse = z.infer<
  typeof consultantBriefingResponseSchema
>;
